/**
 * One logged hour on the project's time sheet, plus the head that names its
 * columns and the menu at the end of the row.
 *
 * Split out of `TimeEntries.tsx` when the "logged by" column and the person
 * filter landed: that file is now the QUERY (what is asked for, what the figures
 * above the list mean, and what the invoice button spends) and this one is the
 * RECORD it draws. The head lives here rather than beside the query because head
 * and row share `TIME_COLS` - a column added to one and not the other slides
 * every cell after it one track sideways, and that is a bug no diff shows once
 * the two halves sit in different files.
 *
 * WHO LOGGED IT is the column this file was made for. `TimeEntry.userId` has
 * been stamped by the route since the module was written and drawn by no screen,
 * ever, which is exactly how 185 production entries came to carry an EMPTY
 * STRING in it: the Perfex import found a single host user and refused to invent
 * owners. So this track is blank on every historic row and fills only from now
 * on - which is a reason to make the blank honest, not a reason to leave the
 * column out. These hours become invoices, and an hour nobody owns is a real
 * thing an operator has to be able to see.
 *
 * AN EMPTY STRING IS NOT AN ID, and the two must not be drawn the same way.
 * `staffLabel` answers an id it cannot resolve with `UNKNOWN_MARK` ("?"), which
 * says "somebody is on this and the directory cannot name them" - a broken row.
 * A time entry with `userId: ""` is the other fact entirely: nobody was ever
 * named. So the empty case is decided BEFORE the directory is asked - no id goes
 * into `UserAvatars` at all, it draws `NOBODY_MARK`, and the wrapper says it in
 * words on hover. That is the same treatment, from the same shared marks, that
 * the assignee column on the tasks tab and the members strip on the overview
 * give the same fact, so one glyph keeps one meaning across the project.
 *
 * Faces rather than names, and a fixed track, for the reason `ProjectTaskRow`
 * spells out: every flexible track raises the width below which the card refuses
 * to draw a list (`listFloor` counts 130px per `fr`), while a disc is 26px
 * whatever the window does. The track is nonetheless 96px, the same as the
 * assignee track next door - not because one face needs it, but because the
 * column heading does: `crm-list-head` sets 10.5px uppercase with 0.13em of
 * letter-spacing, and "LOGGED BY" is 9 characters of it. A narrower track would
 * buy back width the head would then spill out of.
 */
import { Lock, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import type { TimeEntry } from "../../../lib/projects";
import { Pop, useClosePops } from "../../shell/Pop";
import { StatusPill, fmtDate } from "../../ui";
import { UserAvatars } from "../_shared/UserChips";
import { fmtHours } from "./lib";

/** Note, task, who logged it, hours, state, date, actions. */
export const TIME_COLS = "minmax(0,2fr) minmax(0,1.4fr) 96px .7fr 1fr .9fr 44px";

/** One disc, at the size the tasks tab draws its assignees, so the same person
 *  is the same size on both tabs of the same project. */
const FACE_SIZE = 26;

/**
 * The row's menu, or the reason it has none.
 *
 * An invoiced entry used to render an empty cell: the actions vanished and
 * nothing said why, so the operator's reading was "this row is broken" rather
 * than "this row is spoken for". The trigger stays exactly where it is on every
 * other row and the panel carries the sentence instead - `crm-menu-note` is the
 * shared vocabulary for a sentence inside a menu, as opposed to a row you press.
 */
function RowMenu({
  locked,
  onEdit,
  onDelete,
}: {
  locked: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = useT();
  const close = useClosePops();
  return (
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            {locked ? <Lock className="size-4" /> : <MoreHorizontal className="size-4" />}
          </button>
        )}
      >
        {locked ? (
          // Width capped locally: this panel holds one sentence and nothing
          // else, so without a ceiling it would lay the whole thing out on one
          // very wide line.
          <p className="crm-menu-note max-w-[248px]">
            <Lock className="size-3.5" />
            {t("projects.time.invoicedLocked")}
          </p>
        ) : (
          <>
            <button
              type="button"
              className="crm-mi"
              onClick={() => {
                close();
                onEdit();
              }}
            >
              <Pencil className="size-4" />
              {t("projects.time.edit")}
            </button>
            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                onDelete();
              }}
            >
              <Trash2 className="size-4" />
              {t("common.delete")}
            </button>
          </>
        )}
      </Pop>
    </span>
  );
}

/** The column names. `paddingTop` is local rather than in the class because the
 *  head sits directly under a card edge here, with no card title above it. */
export function TimeEntryHead() {
  const t = useT();
  return (
    <div className="crm-list-head" style={{ gridTemplateColumns: TIME_COLS, paddingTop: 22 }}>
      <span>{t("projects.note")}</span>
      <span>{t("projects.time.task")}</span>
      <span>{t("projects.loggedBy")}</span>
      <span>{t("projects.hours")}</span>
      <span>{t("projects.statusCol")}</span>
      <span>{t("projects.col.date")}</span>
      <span />
    </div>
  );
}

export function TimeEntryRow({
  entry,
  onEdit,
  onDelete,
}: {
  entry: TimeEntry;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const t = useT();

  return (
    <div className="crm-list-row" style={{ gridTemplateColumns: TIME_COLS }}>
      {/* The note and the task have a column each now that an entry can carry
          both. They used to share one under the heading "Note", which showed a
          task title to anybody reading the note column. */}
      <span className="crm-prow-n crm-trunc pl-3">{entry.note || "-"}</span>

      <span className="crm-mut crm-trunc text-[12.5px]">{entry.task?.title ?? "-"}</span>

      {/* An empty `userId` is handed in as NO id rather than as an unresolvable
          one - see the file comment. The wrapper's tooltip is set only in that
          case: with a face in the cell `UserAvatars` writes its own from the
          name behind it. */}
      <span
        className="flex min-w-0 items-center"
        title={entry.userId ? undefined : t("projects.unassigned")}
      >
        <UserAvatars ids={entry.userId ? [entry.userId] : []} size={FACE_SIZE} />
      </span>

      <span className="crm-tnum text-[13.25px] font-bold">{fmtHours(entry.minutes)} h</span>

      <span>
        {/* Invoiced beats billable: once it is on an invoice the question "will
            this be charged" is already answered. */}
        <StatusPill tone={entry.invoicedId ? "ok" : entry.billable ? "info" : "mut"}>
          {t(
            entry.invoicedId
              ? "projects.invoicedBadge"
              : entry.billable
                ? "projects.billable"
                : "projects.nonBillable",
          )}
        </StatusPill>
      </span>

      <span className="crm-mut crm-tnum text-[12.5px]">{fmtDate(entry.startedAt)}</span>

      <RowMenu locked={!!entry.invoicedId} onEdit={onEdit} onDelete={onDelete} />
    </div>
  );
}
